import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTrash, FaLock, FaUser, FaUsers, FaBuilding, FaShieldAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * InternalNotePermissionsTab Component
 *
 * Manages which users, roles, or departments can add/view internal (private) notes
 * on action items within a specific board. Mirrors the Access Control tab pattern
 * with a grantee-type selector (Department / Role / User).
 *
 * @param {Object} props Component props
 * @param {number} props.boardId - The board ID to configure permissions for
 */
const InternalNotePermissionsTab = ({ boardId }) => {
    const { apiFetch } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [granting, setGranting] = useState(false);

    // Grant form state
    const [granteeType, setGranteeType] = useState('Department');
    const [selectedGrantee, setSelectedGrantee] = useState('');
    const [canAdd, setCanAdd] = useState(true);
    const [canView, setCanView] = useState(true);

    const fetchPermissions = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/internal-note-permissions`);
            const data = await res.json();
            if (data.success) setPermissions(data.data || []);
        } catch (error) {
            Sentry.captureException(error);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, boardId]);

    const fetchLookupData = useCallback(async () => {
        try {
            const [deptRes, roleRes, userRes] = await Promise.all([
                apiFetch('/api/Department'),
                apiFetch('/api/User/roles'),
                apiFetch('/api/User/view')
            ]);
            const [deptData, roleData, userData] = await Promise.all([
                deptRes.json(), roleRes.json(), userRes.json()
            ]);
            if (deptData.success) setDepartments(deptData.data || []);
            if (roleData.success) setRoles(roleData.data || []);
            if (userData.success) setUsers((userData.data || []).filter(u => u.isActive));
        } catch (error) {
            Sentry.captureException(error);
        }
    }, [apiFetch]);

    useEffect(() => {
        const load = async () => { await Promise.all([fetchPermissions(), fetchLookupData()]); };
        load();
    }, [fetchPermissions, fetchLookupData]);

    const getGranteeName = (type, id) => {
        const numId = parseInt(id);
        switch (type) {
            case 'Department': { const d = departments.find(x => (x.departmentID || x.id) === numId); return d?.departmentName || d?.name || `Department ${numId}`; }
            case 'Role':       { const r = roles.find(x => (x.roleID || x.id) === numId); return r?.roleName || r?.name || `Role ${numId}`; }
            case 'User':       { const u = users.find(x => (x.userID || x.id) === numId); return u ? `${u.firstName} ${u.lastName}` : `User ${numId}`; }
            default:           return `${type} ${numId}`;
        }
    };

    const handleGrant = async () => {
        if (!selectedGrantee) { toast.warning(`Please select a ${granteeType.toLowerCase()}`); return; }
        setGranting(true);
        try {
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/internal-note-permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ granteeType, granteeId: parseInt(selectedGrantee), granteeName: getGranteeName(granteeType, selectedGrantee), canAdd, canView })
            });
            const data = await res.json();
            if (data.success) { toast.success('Permission granted'); setSelectedGrantee(''); await fetchPermissions(); }
            else toast.error(data.message || 'Failed to grant permission');
        } catch (error) { Sentry.captureException(error); toast.error('Failed to grant permission'); }
        finally { setGranting(false); }
    };

    const handleRevoke = async (permissionId) => {
        if (!window.confirm('Revoke this internal note permission?')) return;
        try {
            const res = await apiFetch(`/api/KanbanBoards/${boardId}/internal-note-permissions/${permissionId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) { toast.success('Permission revoked'); await fetchPermissions(); }
            else toast.error(data.message || 'Failed to revoke permission');
        } catch (error) { Sentry.captureException(error); toast.error('Failed to revoke permission'); }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'User':       return <FaUser />;
            case 'Role':       return <FaShieldAlt />;
            case 'Department': return <FaBuilding />;
            default:           return <FaUsers />;
        }
    };

    const getGranteeOptions = () => {
        switch (granteeType) {
            case 'Department': return departments.map(d => ({ value: d.departmentID || d.id, label: d.departmentName || d.name }));
            case 'Role':       return roles.map(r => ({ value: r.roleID || r.id, label: r.roleName || r.name }));
            case 'User':       return users.map(u => ({ value: u.userID || u.id, label: `${u.firstName} ${u.lastName}` }));
            default:           return [];
        }
    };

    if (loading) return <div className="settings-section"><p>Loading permissions...</p></div>;

    return (
        <div className="settings-section internal-note-permissions-tab">
            <h3><FaLock /> Internal Note Permissions</h3>

            <div className="board-settings-permissions__info">
                <FaLock style={{ marginRight: '8px', color: '#e17055' }} />
                <span>
                    Configure which users, roles, or departments can add and view internal (private) notes
                    on action items in this board. Internal notes are hidden from end-users.
                    Admin users and the board owner always have full access.
                </span>
            </div>

            {/* Grant form */}
            <div className="board-settings-permissions__grant-form">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={granteeType} onChange={(e) => { setGranteeType(e.target.value); setSelectedGrantee(''); }}
                        className="board-settings-permissions__select" style={{ minWidth: '130px' }}>
                        <option value="Department">Department</option>
                        <option value="Role">Role</option>
                        <option value="User">User</option>
                    </select>

                    <select value={selectedGrantee} onChange={(e) => setSelectedGrantee(e.target.value)}
                        className="board-settings-permissions__select" style={{ flex: 1, minWidth: '180px' }}>
                        <option value="">-- Select {granteeType} --</option>
                        {getGranteeOptions().map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    <label className="checkbox-label" style={{ whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={canAdd} onChange={(e) => { setCanAdd(e.target.checked); if (e.target.checked) setCanView(true); }} />
                        Can Add
                    </label>
                    <label className="checkbox-label" style={{ whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={canView} onChange={(e) => { setCanView(e.target.checked); if (!e.target.checked) setCanAdd(false); }} />
                        Can View
                    </label>

                    <button onClick={handleGrant} disabled={granting || !selectedGrantee} className="board-settings-permissions__grant-btn">
                        <FaPlus /> Grant Access
                    </button>
                </div>
            </div>

            {/* Existing permissions list */}
            <div className="board-settings-permissions__list">
                {permissions.length === 0 ? (
                    <p className="board-settings-permissions__empty">
                        No internal note permissions configured yet. Add a user, role, or department above to enable internal notes on this board.
                    </p>
                ) : (
                    <table className="board-settings-permissions__table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Type</th>
                                <th style={{ textAlign: 'left' }}>Name</th>
                                <th style={{ textAlign: 'left' }}>Can Add</th>
                                <th style={{ textAlign: 'left' }}>Can View</th>
                                <th style={{ textAlign: 'left' }}>Scope</th>
                                <th style={{ textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {permissions.map(perm => (
                                <tr key={perm.permissionId}>
                                    <td>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {getTypeIcon(perm.granteeType || 'Department')}
                                            {perm.granteeType || 'Department'}
                                        </span>
                                    </td>
                                    <td>{perm.granteeName || perm.departmentName || `ID: ${perm.granteeId ?? perm.departmentId}`}</td>
                                    <td><span className={`permission-badge ${perm.canAdd ? 'yes' : 'no'}`}>{perm.canAdd ? '✓ Yes' : '✗ No'}</span></td>
                                    <td><span className={`permission-badge ${perm.canView ? 'yes' : 'no'}`}>{perm.canView ? '✓ Yes' : '✗ No'}</span></td>
                                    <td><span className={`scope-badge ${perm.isGlobal ? 'global' : 'board'}`}>{perm.isGlobal ? '🌐 Global' : '📋 This Board'}</span></td>
                                    <td>
                                        {!perm.isGlobal && (
                                            <button className="btn-icon-danger" onClick={() => handleRevoke(perm.permissionId)} title="Revoke permission">
                                                <FaTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default InternalNotePermissionsTab;
